import { asFunction, callGetterFor } from "../../helpers/functional-util.js";

/**
 * `gridColumnSpecification` defines how to render a grid column from a
 * given model.
 *
 * @param {string} spec.name - Name of the column
 * @param {string} spec.sortCriterion - String use to order models
 * If specified, it makes the column "orderable"
 * @param {function} [spec.renderer] - Function returning the cell content
 * given a model
 * @param {string} spec.type - Type of the column among 'text', 'number',
 * 'amount', 'other'. 'text' by default
 * @param {function} spec.cssClass - Function returning the css class of a
 * cell given a model
 * @param {string} spec.headerCssClass - Css class of the column th
 * @param {string} spec.dataTestName - Value for the "data-test" html attribute in each cell.
 */
export default class GridColumnSpecification {
	constructor(spec) {
		this._name = spec.name;
		this._sortCriterion = spec.sortCriterion;
		this._renderer = spec.renderer || this._defaultRenderer;
		this._type = spec.type || "text";
		this._cssClass = spec.cssClass || (() => "");
		this._headerCssClass = spec.headerCssClass || "";
		this._dataTestName = spec.dataTestName || "";
	}

	getName() {
		return this._name;
	}

	getSortCriterion() {
		return this._sortCriterion;
	}

	getRenderer() {
		return this._renderer;
	}

	getHeaderCssClass() {
		return this._headerCssClass;
	}

	getDataTestName() {
		return this._dataTestName;
	}

	getType() {
		return this._type;
	}

	isSortable() {
		return !!this._sortCriterion;
	}

	cssClass(model) {
		const cssClass = asFunction(this._cssClass);
		return cssClass(model);
	}

	renderer(model) {
		let value;

		try {
			value = callGetterFor(model, this._sortCriterion);
		} catch {
			value = undefined;
		}

		// It can be ok to pass undefined if `this._renderer` is not the default one.
		return this._renderer(model, value);
	}

	_defaultRenderer(model, value) {
		if (value === undefined) {
			throw new Error("Cannot fetch a value to render by default");
		}
		return function (html) {
			html.render(value);
		};
	}
}
